import * as XLSX from 'xlsx-js-style';
import type { PnLReportData } from './types';
import {
  filterTreeByGastosView,
  formatNumberPdf,
  formatVariationPdf,
  getLatestBudget,
  getLatestVariation,
  getNumeric,
  getReportColumns,
} from './utils';

const PRIMARY = '0F172A';
const SUCCESS = '059669';
const SUCCESS_LIGHT = 'D1FAE5';
const DANGER = 'DC2626';
const DANGER_BG = 'FEF2F2';
const SUCCESS_BG = 'ECFDF5';
const MUTED = '64748B';
const BORDER = 'E2E8F0';

function createCell(value: string | number, style?: Record<string, unknown>): XLSX.CellObject {
  const cell: XLSX.CellObject = {
    v: value,
    t: typeof value === 'number' ? 'n' : 's',
  };
  if (style) {
    cell.s = style as never;
  }
  return cell;
}

function applyTitle(ws: XLSX.WorkSheet, r: number, c: number, value: string) {
  ws[XLSX.utils.encode_cell({ r, c })] = createCell(value, {
    font: { bold: true, sz: 16, color: { rgb: PRIMARY } },
    alignment: { horizontal: 'left', vertical: 'center' },
  });
}

function applySubtitle(ws: XLSX.WorkSheet, r: number, c: number, value: string) {
  ws[XLSX.utils.encode_cell({ r, c })] = createCell(value, {
    font: { sz: 10, color: { rgb: MUTED } },
    alignment: { horizontal: 'left', vertical: 'center' },
  });
}

function applyHeader(ws: XLSX.WorkSheet, r: number, c: number, value: string) {
  ws[XLSX.utils.encode_cell({ r, c })] = createCell(value, {
    fill: { patternType: 'solid', fgColor: { rgb: PRIMARY } },
    font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: BORDER } },
    },
  });
}

function applySection(ws: XLSX.WorkSheet, r: number, c: number, value: string, colCount: number) {
  ws[XLSX.utils.encode_cell({ r, c })] = createCell(value, {
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    font: { bold: true, sz: 10, color: { rgb: PRIMARY } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: BORDER } },
      bottom: { style: 'thin', color: { rgb: BORDER } },
    },
  });

  for (let i = 1; i <= colCount; i++) {
    ws[XLSX.utils.encode_cell({ r, c: i })] = createCell('', {
      fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
      border: {
        top: { style: 'thin', color: { rgb: BORDER } },
        bottom: { style: 'thin', color: { rgb: BORDER } },
      },
    });
  }
}

function setRange(ws: XLSX.WorkSheet, endRow: number, endCol: number) {
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: endRow, c: endCol },
  });
}

export function exportPnLToExcel(data: PnLReportData, filename?: string) {
  const wb = XLSX.utils.book_new();

  const tree = filterTreeByGastosView(data.tree, data.gastosView);
  const reportColumns = getReportColumns(data.periodColumns, data.selectedMonths);
  const realColumns = reportColumns.filter(c => !c.isBudget && c.date);
  const hasBudget = reportColumns.some(c => c.isBudget);
  const headers = [
    'CONCEPTO',
    ...realColumns.map(c =>
      data.preliminarMonths?.includes(c.yearMonth)
        ? `${c.yearMonth} (Preliminar)`
        : c.yearMonth
    ),
  ];
  if (hasBudget) headers.push('PRESUPUESTO');
  headers.push('% VS MES ANT.');

  const colCount = headers.length - 1;

  const ws: XLSX.WorkSheet = {
    '!ref': 'A1:A1',
    '!cols': [
      { wch: 48 },
      ...realColumns.map(() => ({ wch: 18 })),
      ...(hasBudget ? [{ wch: 18 }] : []),
      { wch: 14 },
    ],
  };

  let r = 0;
  applyTitle(ws, r, 0, 'Estado de Resultados');
  r += 1;
  applySubtitle(ws, r, 0, `${data.companyName} · ${data.periodLabel}`);
  r += 2;

  headers.forEach((h, c) => applyHeader(ws, r, c, h));
  r += 1;

  tree.forEach(section => {
    applySection(ws, r, 0, `${section.label}  ${section.children?.length ?? 0} conceptos`, colCount);
    r += 1;

    section.children?.forEach((child, idx) => {
      const isTotal = child.isTotal;
      const indent = Math.min(child.level + 1, 4);
      const bgColor = isTotal ? SUCCESS_LIGHT : idx % 2 === 0 ? 'FFFFFF' : 'F8FAFC';
      const variation = getLatestVariation(child.row, reportColumns);
      const budget = hasBudget ? getLatestBudget(child.row, reportColumns) : null;

      ws[XLSX.utils.encode_cell({ r, c: 0 })] = createCell(child.label, {
        fill: { patternType: 'solid', fgColor: { rgb: bgColor } },
        font: { bold: isTotal, sz: 10, color: { rgb: PRIMARY } },
        alignment: { horizontal: 'left', vertical: 'center', indent },
        border: {
          bottom: { style: 'hair', color: { rgb: BORDER } },
        },
      });

      let c = 1;
      realColumns.forEach(col => {
        const value = getNumeric(child.row, col.key);
        ws[XLSX.utils.encode_cell({ r, c })] = createCell(value !== null ? formatNumberPdf(value) : '—', {
          fill: { patternType: 'solid', fgColor: { rgb: bgColor } },
          font: { bold: isTotal, sz: 10, color: { rgb: PRIMARY } },
          alignment: { horizontal: 'right', vertical: 'center' },
          border: {
            bottom: { style: 'hair', color: { rgb: BORDER } },
          },
        });
        c += 1;
      });

      if (hasBudget) {
        ws[XLSX.utils.encode_cell({ r, c })] = createCell(budget !== null ? formatNumberPdf(budget) : '—', {
          fill: { patternType: 'solid', fgColor: { rgb: bgColor } },
          font: { bold: isTotal, sz: 10, color: { rgb: PRIMARY } },
          alignment: { horizontal: 'right', vertical: 'center' },
          border: {
            bottom: { style: 'hair', color: { rgb: BORDER } },
          },
        });
        c += 1;
      }

      let varColor = PRIMARY;
      let varBg = bgColor;
      if (variation !== null && Number.isFinite(variation)) {
        varColor = variation >= 0 ? DANGER : SUCCESS;
        varBg = variation >= 0 ? DANGER_BG : SUCCESS_BG;
      }

      ws[XLSX.utils.encode_cell({ r, c })] = createCell(formatVariationPdf(variation), {
        fill: { patternType: 'solid', fgColor: { rgb: varBg } },
        font: { bold: true, sz: 10, color: { rgb: varColor } },
        alignment: { horizontal: 'right', vertical: 'center' },
        border: {
          bottom: { style: 'hair', color: { rgb: BORDER } },
        },
      });

      r += 1;
    });

    r += 1;
  });

  setRange(ws, r - 1, colCount);
  XLSX.utils.book_append_sheet(wb, ws, 'Estado de Resultados');

  XLSX.writeFile(
    wb,
    filename ?? `estado-resultados-${data.companyName}-${data.periodLabel}.xlsx`
  );
}
